import React, {
    FormEvent,
    useState,
    useRef,
    useEffect,
    useContext,
} from "react";

import {
    useNavigate,
    useLocation,
    useParams,
    Link as RouterLink,
} from "react-router-dom";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";

import { FormField, PasswordField } from "../components/InputFields";
import Button from "../components/Button";

import { useService as useUsersService } from "../services/Users";
import { ActionType, Context as AuthContext } from "../context/Auth";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Signup = () => {
    const navigate = useNavigate();

    const service = useUsersService();

    const { dispatch: authDispatch } = useContext(AuthContext);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");

    const [phoneNumber, setPhoneNumber] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showPassword, setShowPassword] = useState(false);

    const [loading, setLoading] = useState(false);

    const confirmRef = useRef<HTMLInputElement>();
    const emailRef = useRef<HTMLInputElement>();

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            return;
        }

        try {
            setLoading(true);

            // Check the recaptcha
            // const recaptchaToken = await recaptchaRef.current.executeAsync();

            const fixedEmail = email.toLowerCase().trim();

            const event = {
                firstName,
                lastName,
                email: fixedEmail,
            };

            await service.create({
                ...event,
                password,
            });

            navigate("/dashboard");
        } catch (err) {
            console.error(err);
        }

        setLoading(false);
    };

    useEffect(() => {
        const match = password === confirmPassword;

        if (confirmRef.current) {
            confirmRef.current.setCustomValidity(
                match ? "" : "Passwords do not match.",
            );
        }
    }, [password, confirmPassword]);

    useEffect(() => {
        const valid = EMAIL_REGEX.test(email);
        if (emailRef.current) {
            emailRef.current.setCustomValidity(
                valid
                    ? ""
                    : `Please enter an email in the form of 'name@example.com'`,
            );
        }
    }, [email]);

    return (
        <form onSubmit={onSubmit}>
            {/* <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={RECAPTCHA_SITE_KEY}
            /> */}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                }}
            >
                <Box display="flex" justifyContent="center" width="100%">
                    <Stack
                        spacing={2}
                        sx={{
                            width: {
                                xs: "85%", // mobile
                                sm: "66%", // ~8/12
                                md: "50%", // ~6/12
                                lg: "33%", // ~4/12
                            },
                        }}
                    >
                        <FormField
                            required
                            type="text"
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            autoFocus
                        />
                        <FormField
                            required
                            type="text"
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />

                        <FormField
                            required
                            type="email"
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            inputRef={emailRef}
                        />

                        <PasswordField
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                        />

                        <PasswordField
                            inputRef={confirmRef}
                            label="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            showPassword={showPassword}
                        />

                        <Box width="100%">
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                size="large"
                                fullWidth
                            >
                                Sign Up
                            </Button>
                        </Box>

                        <Box mt={4} />

                        <Typography align="center" variant="body1">
                            Already have an account?{" "}
                            <Link component={RouterLink} to="/login">
                                Sign in
                            </Link>
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            <input type="hidden" id="zc_gad" name="zc_gad" value="" />
        </form>
    );
};

export default Signup;
